import { GetServerSidePropsContext } from 'next';
import { parseContextLocale } from 'utils/parseContextLocale';
import { parseRouteParams } from 'utils/parseRouteParams';
import { MarketType } from 'constants/markets';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { SelfRegisterCustomerForm } from '../../components/Customer/SelfRegisterCustomerForm';
import { getCommonServerSideProps } from '../../server/getCommonServerSideProps';

import BigBanner from '../../../public/big-banner.png';
import { ProfileLayout } from '../../components/Consultant/ProfileLayout';
import { ProfilePageContent, ProfileProps } from '../../components/Consultant/ProfilePageContent';
import PageMeta from '../../components/PageMeta';
import { getConsultantPublicContentById, getConsultantPublicContentBySlug } from '../../graphql/consultantQueries';

type PageProps = ProfileProps;

export default function Page(props: PageProps) {
    const { t } = useServerTranslation();

    const { publicContent } = props;

    const fullName = `${publicContent.first_name} ${publicContent.last_name}`;

    return (
        <ProfileLayout backgroundImageSrc={BigBanner.src}>
            <PageMeta
                title={t('seo.title.consultant-profile-page', { fullName })}
            />
            <ProfilePageContent {...props}>
                <SelfRegisterCustomerForm
                    consultantId={publicContent.id}
                    boxProps={{ sx: { maxWidth: '670px', fontSize: 12 } }}
                />
            </ProfilePageContent>
        </ProfileLayout>
    );
}

type RouteParams = {
    idOrSlug: string;
};

function getConsultantByIdOrSlug(market: MarketType, idOrSlug: string) {
    if (Number.isNaN(Number(idOrSlug))) {
        return getConsultantPublicContentBySlug(market, idOrSlug);
    }

    return getConsultantPublicContentById(market, Number(idOrSlug));
}

export async function getServerSideProps(context: GetServerSidePropsContext<RouteParams>) {
    const [idOrSlug] = parseRouteParams(context, ['idOrSlug']);
    const { market } = await parseContextLocale(context);
    const result = await getConsultantByIdOrSlug(market, idOrSlug);

    if (!result?.data?.organisationPublicContent) {
        return { notFound: true };
    }

    return getCommonServerSideProps({
        context,
        additionalProps: {
            publicContent: result?.data?.organisationPublicContent || '',
            id: result.data.organisationPublicContent.id,
        },
    });
}
